import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { optimizeImage } from "@/lib/image-optimizer";

// Default fallback image for medical/dental content
const DEFAULT_FALLBACK = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=450&fit=crop";

interface LazyImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  srcset?: string;
  sizes?: string;
  className?: string;
  fallbackClassName?: string;
  fallbackIcon?: React.ReactNode;
  aspectRatio?: "video" | "square" | "portrait" | "auto";
  priority?: boolean;
}

export function LazyImage({
  src,
  alt,
  width,
  height,
  srcset,
  sizes,
  className,
  fallbackClassName,
  fallbackIcon,
  aspectRatio = "video",
  priority = false,
}: LazyImageProps) {
  // Use fallback if src is empty
  const rawSrc = src && src.trim() !== '' ? src : DEFAULT_FALLBACK;
  // Apply standard optimization (compression, format) without forced resizing
  const imageSrc = optimizeImage(rawSrc); // No width/height passed, just standard params

  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "100px",
        threshold: 0.01,
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const aspectRatioClasses = {
    video: "aspect-video",
    square: "aspect-square",
    portrait: "aspect-[3/4]",
    auto: "",
  };

  useEffect(() => {
    if (!isInView || isLoaded || hasError) return;

    // Timeout fallback for slow images (5 seconds)
    const timeoutId = setTimeout(() => {
      if (!isLoaded) {
        setHasError(true);
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [isInView, isLoaded, hasError]);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(false);
  };

  return (
    <div
      ref={imgRef}
      className={cn(
        "relative overflow-hidden bg-muted",
        aspectRatioClasses[aspectRatio],
        className
      )}
    >
      {/* Skeleton/Placeholder */}
      {(!isLoaded || hasError) && (
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50",
            !isLoaded && !hasError && "animate-pulse",
            fallbackClassName
          )}
        >
          {fallbackIcon}
        </div>
      )}

      {/* Actual Image */}
      {isInView && !hasError && (
        <img
          src={imageSrc}
          alt={alt}
          {...(width && { width })}
          {...(height && { height })}
          {...(srcset && { srcSet: srcset })}
          {...(sizes && { sizes })}
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            isLoaded ? "blur-loaded opacity-100" : "blur-placeholder opacity-0"
          )}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding="async"
        />
      )}
    </div>
  );
}

